import type { CostableLine } from '@/lib/sales/document-costing';

/**
 * A job's stored lines, read as the costing module reads any document's lines.
 *
 * A job carries the SAME cost figures its estimate did — `estimateLineToJobLineSnapshot`
 * copies `costBasis`, `markupPercent`, `quantity`, `estimatedHours` and the labor
 * rate onto every converted line, so a service's included material arrives on the
 * job as a real line with a real cost. NOT-634 was reported as "the estimate's
 * cost does not carry to the job"; it carried. Nothing on the job's Costs tab
 * ever asked the lines what they cost.
 *
 * This module exists so that asking is one hop, in one place. `Job.lineItems` is
 * typed `Record<string, unknown>` — it is a JSON snapshot written by the pricing
 * engine, in whichever spelling the writer used — and the costing module wants a
 * `CostableLine`. Every surface that translates between the two on its own is a
 * surface that can quietly stop reading a field, which is exactly how a priced
 * material becomes a $0 cost on one screen and not on another.
 */

type JobLineRecord = Record<string, unknown>;

const asNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value ?? fallback);
  return Number.isFinite(n) ? n : fallback;
};

const asOptionalNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * What the job BILLS for one unit of this line.
 *
 * Cost never reads it — `costDocument` costs a part from `costBasis` and a
 * service from its hours — so it is here only because `CostableLine` states a
 * price and a mapper that invented one would be lying about the line. The
 * operator's override outranks the pricebook's figure, same order as
 * `unitPriceOf` in line-economics.
 */
function unitPriceOf(item: JobLineRecord): number {
  return asNumber(
    item.manualPriceOverride ?? item.unitPrice ?? item.price ?? item.basePrice ?? item.laborRate,
    0,
  );
}

/**
 * How many the job is costing.
 *
 * The ACTUAL consumption when a visit has reported one, because that is what the
 * job really used; the quoted quantity until then. A service ignores this
 * entirely — `lineCost` costs a service by its time — so passing an actual hour
 * count here can never turn into `hours x quantity`.
 */
function quantityOf(item: JobLineRecord): number {
  const actual = asOptionalNumber(
    item.actualHours ??
      item.actual_hours ??
      item.actualQty ??
      item.actual_qty ??
      item.actualQuantity ??
      item.actual_quantity,
  );
  return actual ?? asNumber(item.quantity ?? item.qty, 1);
}

/** One stored job line, in the shape the costing module answers questions about. */
export function toCostableJobLine(item: JobLineRecord): CostableLine {
  return {
    type: asString(item.type) || asString(item.itemType),
    quantity: quantityOf(item),
    basePrice: unitPriceOf(item),
    costBasis: asNumber(item.costBasis ?? item.cost_basis, 0),
    // The rate the recorded time is WORTH, never `laborRate` — the invoice write
    // path back-fills that one from the unit price, so costing from it would
    // report cost = price and pin every hourly service at 0% margin.
    laborCostRate: asOptionalNumber(item.laborCostRate ?? item.labor_cost_rate),
    priceStrategy: asString(item.priceStrategy ?? item.pricingStrategy) || undefined,
    estimatedHours: asOptionalNumber(item.estimatedHours ?? item.estimated_hours),
    // A materialized dynamic service stores `costBasis` 0 by design and keeps its
    // real time in here; without the config it reads as free.
    dynamicPricingConfig: (item.dynamicPricingConfig ??
      item.dynamic_pricing_config ??
      null) as CostableLine['dynamicPricingConfig'],
    // A margin-share labor row records its percentage nowhere else — frozen onto
    // the row when the service was picked — so a job line that dropped the rows
    // costed its crew's share at $0 while the estimate beside it did not.
    laborCostLines: Array.isArray(item.laborCostLines)
      ? (item.laborCostLines as CostableLine['laborCostLines'])
      : undefined,
    // An optional line the customer declined was offered, not sold. BOTH ticks,
    // because "unselected" only takes a line out of the sale when the line is an
    // OFFER — the rule the price side applies (`isLineSold`), and the server's:
    // `customerApprove` rejects a required line submitted as deselected.
    isOptional: Boolean(item.isOptional ?? item.is_optional),
    isSelected: item.isSelected !== false,
    // The service ↔ part link, so the roll-up can see that a water heater
    // belongs to an install the customer declined (NOT-635). Dropping it left
    // this tab costing the parts of work nobody bought.
    lineKey: asString(item.lineKey ?? item.line_key) || undefined,
    parentLineKey: asString(item.parentLineKey ?? item.parent_line_key) || undefined,
    // Never read as a cost — read to tell WHOSE sale a part is in. A part billed
    // on its own line survives its service being declined, because the engine
    // goes on charging for it; one recovered through the service does not.
    ...(typeof item.chargeable === 'boolean' ? { chargeable: item.chargeable } : {}),
    pricingTreatment:
      (asString(item.pricingTreatment ?? item.pricing_treatment) as
        | CostableLine['pricingTreatment']
        | '') || undefined,
  };
}

/** A job's whole basket, ready to cost. */
export function jobCostableLines(items: JobLineRecord[] | undefined | null): CostableLine[] {
  return (items ?? []).map(toCostableJobLine);
}
